import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FlaskConical, Activity, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { DataTable, type Column } from "@/components/DataTable";
import { DetailDrawer, type DrawerField } from "@/components/DetailDrawer";

export const Route = createFileRoute("/admin/administrateur/lab/resultats/")({
  component: Page,
});

interface LabResult {
  id: number;
  patient_id: number;
  loinc_code: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  reference_low: number | null;
  reference_high: number | null;
  abnormal_flag: string | null;
  status: string;
  resulted_at: string | null;
  analyzer: string | null;
}

function Page() {
  const [data, setData] = useState<LabResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{ abnormal_count: number; by_status: Record<string, number> } | null>(null);
  const [drawer, setDrawer] = useState<LabResult | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r, s] = await Promise.all([api.labResults({ limit: 200 }), api.labStats()]);
        setData((r as any).items);
        setStats(s as any);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const columns: Column<LabResult>[] = [
    { key: "id", label: "#", width: "60px" },
    { key: "loinc_code", label: "LOINC", render: (r) => <code className="text-[11px] bg-black/30 px-1 py-0.5 rounded">{r.loinc_code}</code> },
    { key: "test_name", label: "Test", sortable: true },
    { key: "patient_id", label: "Patient", render: (r) => `P${String(r.patient_id).padStart(4, "0")}` },
    { key: "value", label: "Valeur", sortable: true, render: (r) => <span className="font-mono">{r.value ?? "—"} {r.unit}</span> },
    { key: "reference_low", label: "Ref", render: (r) => <span className="text-xs text-muted-foreground">{r.reference_low ?? "—"}–{r.reference_high ?? "—"}</span> },
    { key: "abnormal_flag", label: "Flag", render: (r) => r.abnormal_flag ? <Badge variant="destructive">{r.abnormal_flag === "H" ? "Haut" : "Bas"}</Badge> : <Badge variant="outline">N</Badge> },
    { key: "status", label: "Statut", tone: (r) => r.status === "validated" ? "default" : r.status === "amended" ? "destructive" : "secondary" },
    { key: "analyzer", label: "Analyseur", render: (r) => <span className="text-xs">{r.analyzer}</span> },
    { key: "resulted_at", label: "Date", render: (r) => r.resulted_at ? <span className="text-xs text-muted-foreground">{new Date(r.resulted_at).toLocaleString("fr-FR")}</span> : "—" },
  ];

  const drawerFields: DrawerField[] = drawer ? [
    { label: "ID", value: String(drawer.id), mono: true },
    { label: "Patient", value: `P${String(drawer.patient_id).padStart(4, "0")}` },
    { label: "Test", value: drawer.test_name },
    { label: "Code LOINC", value: drawer.loinc_code, mono: true },
    { label: "Valeur", value: `${drawer.value ?? "—"} ${drawer.unit ?? ""}`, mono: true },
    { label: "Référence", value: `${drawer.reference_low ?? "—"} – ${drawer.reference_high ?? "—"} ${drawer.unit ?? ""}` },
    { label: "Anomalie", value: drawer.abnormal_flag || "Normale", tone: drawer.abnormal_flag ? "destructive" : "default" },
    { label: "Statut", value: drawer.status, tone: drawer.status === "validated" ? "default" : "secondary" },
    { label: "Analyseur", value: drawer.analyzer || "—" },
    { label: "Date résultat", value: drawer.resulted_at ? new Date(drawer.resulted_at).toLocaleString("fr-FR") : "—" },
  ] : [];

  return (
    <div className="space-y-4 p-6">
      <Link to="/admin/administrateur" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Dashboard Administrateur
      </Link>
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FlaskConical className="h-6 w-6" /> Résultats laboratoire
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">LOINC + validation biologique + contrôle qualité · données live</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Total</div><div className="mt-1 text-2xl font-bold">{data.length}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Anomalies</div><div className="mt-1 text-2xl font-bold text-red-400">{stats.abnormal_count}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Validés</div><div className="mt-1 text-2xl font-bold text-emerald-400">{stats.by_status.validated || 0}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">En attente</div><div className="mt-1 text-2xl font-bold text-amber-400">{stats.by_status.pending || 0}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Amendés</div><div className="mt-1 text-2xl font-bold text-orange-400">{stats.by_status.amended || 0}</div></CardContent></Card>
        </div>
      )}

      <DataTable
        title="Résultats"
        subtitle="Cliquez sur une ligne pour voir le détail"
        data={data}
        columns={columns}
        loading={loading}
        searchFields={["test_name", "loinc_code", "patient_id", "analyzer", "status"]}
        onRowClick={(r) => setDrawer(r as LabResult)}
      />

      <DetailDrawer
        open={!!drawer}
        onOpenChange={(o) => !o && setDrawer(null)}
        title={drawer?.test_name || ""}
        subtitle={drawer ? `LOINC ${drawer.loinc_code} · Patient P${String(drawer.patient_id).padStart(4, "0")}` : ""}
        badge={drawer ? { label: drawer.status, tone: drawer.abnormal_flag ? "destructive" : "default" } : undefined}
        fields={drawerFields}
      />
    </div>
  );
}